import { useCallback, useEffect, useRef, useState } from "react";
import { appendSegments, appendSpokenSegments, closeUtterance, collectNewFinals } from "./speechTranscript";
import { describeSpeechEvent, deviceSummary, isSpeechDebugEnabled } from "./speechDiagnostics";

/**
 * One dictation lifecycle for every CrewPilot surface that shows a transcript
 * (Build Tasks with Ashley, Project Conversations). FINAL RESULTS ONLY:
 *
 *   browser recognition (interimResults = false) → finalized results → this hook → transcript
 *
 *  - ONE live recognition session at a time; starting another ends the previous one first.
 *  - A session's handlers only act while that session is still the live one, and are detached
 *    when it is replaced / aborted / unmounted, so a stopped session can never write again.
 *  - Each finalized result is appended to the existing text once (by result index).
 *    Recording again appends to what is already there.
 *  - `takeOver()` ends dictation when the user edits the text by hand.
 *
 * getText(): current text of the box (read when recording starts).
 * onText(text): receive the new full text.  onError(): recognition failed (captured text stays).
 *
 * holdSession: the user, not browser silence, ends the session. A browser `end` restarts
 * recognition until Stop, unmount, permission failure, or an unrecoverable error.
 * Task Builder opts in. Other callers keep the original one-shot session.
 */
const RECOVERABLE_ERRORS = new Set(["no-speech"]);
const RAPID_END_MS = 400;
const RAPID_END_LIMIT = 5;
// Yield so the browser can finish the instance that just ended. This is not a silence timeout.
const RESTART_DELAY_MS = 50;
function createSession() {
  const Recognition = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
  if (!Recognition) return null;
  const session = new Recognition();
  session.continuous = true;
  session.interimResults = false;
  return session;
}

function release(session, { abort }) {
  if (!session) return;
  session.onresult = null;
  session.onerror = null;
  session.onend = null;
  try {
    if (abort) session.abort?.();
    else session.stop?.();
  } catch {
    // already ended
  }
}

export function useDictation({ getText, onText, onError, holdSession = false }) {
  const [recording, setRecording] = useState(false);
  // Temporary diagnostics (only when opened with ?speechdebug=1): raw recognition events.
  const [debugEnabled] = useState(() => isSpeechDebugEnabled());
  const [debugLines, setDebugLines] = useState([]);
  const debug = useRef({ seq: 0, startedAt: 0 });
  const note = useCallback(
    (line) => {
      if (debugEnabled) setDebugLines((lines) => [...lines.slice(-400), line]);
    },
    [debugEnabled],
  );
  const live = useRef(null);
  // While a session is live this hook is the only writer (typing ends it via takeOver),
  // so it keeps the running text itself instead of re-reading possibly stale props.
  const text = useRef("");
  const callbacks = useRef({ getText, onText, onError });
  callbacks.current = { getText, onText, onError };
  const userListening = useRef(false);
  const mounted = useRef(true);
  const fatal = useRef(false);
  const epoch = useRef(0);
  const rapidEnds = useRef(0);
  const restartTimer = useRef(null);
  const beginRef = useRef(() => false);

  const clearRestart = useCallback(() => {
    if (restartTimer.current != null) {
      clearTimeout(restartTimer.current);
      restartTimer.current = null;
    }
  }, []);

  const endUserSession = useCallback(() => {
    userListening.current = false;
    epoch.current += 1;
    clearRestart();
  }, [clearRestart]);

  useEffect(
    () => () => {
      mounted.current = false;
      userListening.current = false;
      epoch.current += 1;
      clearRestart();
      release(live.current, { abort: true });
      live.current = null;
    },
    [clearRestart],
  );

  const beginInstance = useCallback((seed) => {
    if (!mounted.current) return false;
    if (holdSession && !userListening.current) return false;
    const session = createSession();
    if (!session) return false;
    release(live.current, { abort: true });
    live.current = session;
    if (seed) text.current = callbacks.current.getText?.() || "";
    const seen = new Set(); // result indexes already appended in THIS recognition instance
    const instance = { baseLength: text.current.length, closed: false, produced: false, startedAt: Date.now() };
    if (debugEnabled && seed) {
      debug.current = { seq: 0, startedAt: typeof performance !== "undefined" ? performance.now() : Date.now() };
      note(`START ${deviceSummary()} base=${JSON.stringify(text.current)}`);
    } else if (debugEnabled) {
      note("RESTART");
    }
    const closeInstance = () => {
      if (!holdSession || instance.closed) return;
      instance.closed = true;
      const next = closeUtterance(text.current, instance.baseLength);
      if (next === text.current) return;
      text.current = next;
      callbacks.current.onText?.(text.current);
    };
    session.onresult = (event) => {
      if (live.current !== session) return;
      const segments = collectNewFinals(event, seen);
      if (debugEnabled) {
        debug.current.seq += 1;
        note(describeSpeechEvent(debug.current.seq, debug.current.startedAt, event, segments));
      }
      if (!segments.length) return;
      instance.produced = true;
      rapidEnds.current = 0;
      text.current = holdSession
        ? appendSpokenSegments(text.current, segments)
        : appendSegments(text.current, segments);
      callbacks.current.onText?.(text.current);
    };
    session.onerror = (event) => {
      if (live.current !== session) return;
      const code = event?.error || "";
      note(`ERROR ${code}`);
      if (code === "aborted") return;
      if (holdSession && RECOVERABLE_ERRORS.has(code)) return;
      fatal.current = true;
      endUserSession();
      live.current = null;
      setRecording(false);
      callbacks.current.onError?.();
    };
    session.onend = () => {
      if (live.current !== session) return;
      note("END");
      const keep = holdSession && userListening.current && mounted.current && !fatal.current;
      closeInstance();
      live.current = null;
      if (!keep) {
        setRecording(false);
        return;
      }
      const rapid = !instance.produced && Date.now() - instance.startedAt < RAPID_END_MS;
      if (rapid) rapidEnds.current += 1;
      else rapidEnds.current = 0;
      if (rapidEnds.current >= RAPID_END_LIMIT) {
        fatal.current = true;
        endUserSession();
        setRecording(false);
        callbacks.current.onError?.();
        return;
      }
      if (!userListening.current || !mounted.current || fatal.current) return;
      const token = epoch.current;
      clearRestart();
      restartTimer.current = setTimeout(() => {
        restartTimer.current = null;
        if (token !== epoch.current || !userListening.current || !mounted.current || fatal.current) return;
        beginRef.current(false);
      }, RESTART_DELAY_MS);
    };
    try {
      session.start();
    } catch {
      fatal.current = true;
      endUserSession();
      live.current = null;
      setRecording(false);
      callbacks.current.onError?.();
      return false;
    }
    setRecording(true);
    return true;
  }, [clearRestart, debugEnabled, endUserSession, holdSession, note]);
  beginRef.current = beginInstance;

  /** Returns false when this browser has no speech recognition. */
  const start = useCallback(() => {
    fatal.current = false;
    rapidEnds.current = 0;
    if (holdSession) userListening.current = true;
    epoch.current += 1;
    const ok = beginInstance(true);
    if (!ok) endUserSession();
    return ok;
  }, [beginInstance, endUserSession, holdSession]);

  /**
   * Explicit Stop. The user session ends before the browser `end` event, so that
   * event cannot restart recognition. `stop()` (not abort) still lets the engine
   * deliver its last final result; punctuation is applied when that `end` arrives.
   */
  const stop = useCallback(() => {
    if (live.current) note("STOP requested");
    userListening.current = false;
    epoch.current += 1;
    clearRestart();
    live.current?.stop?.();
    setRecording(false);
  }, [clearRestart, note]);

  /** The user typed: end dictation so a late recognition event cannot overwrite the edit. */
  const takeOver = useCallback(() => {
    if (!live.current && !userListening.current) return;
    endUserSession();
    release(live.current, { abort: true });
    live.current = null;
    setRecording(false);
  }, [endUserSession]);

  const clearDebug = useCallback(() => setDebugLines([]), []);

  return { recording, start, stop, takeOver, debug: debugEnabled ? { lines: debugLines, clear: clearDebug } : null };
}
